import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { SiteProvider } from './context/SiteContext';
import Layout from './components/Layout';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Assets from './pages/Assets';
import AssetDetail from './pages/AssetDetail';
import WorkOrders from './pages/WorkOrders';
import WorkOrderDetail from './pages/WorkOrderDetail';
import MyWork from './pages/MyWork';
import AdminPanel from './pages/AdminPanel';
import ManageUsers from './pages/ManageUsers';
import Settings from './pages/Settings';
import Reports from './pages/Reports';
import SupervisorPanel from './pages/SupervisorPanel';
import MyTeam from './pages/MyTeam';
import ActivateAccount from './pages/ActivateAccount';
import ResetPassword from './pages/ResetPassword';
import HelpCentre from './pages/HelpCentre';
import CreateNotification from './pages/CreateNotification';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import ErrorBoundary from './components/ErrorBoundary';
import DisabledScreen from './components/DisabledScreen';
import NotFound from './pages/NotFound';
import Credentials from './pages/Credentials';
import CompanyCode from './pages/CompanyCode';
import { ToastProvider } from './context/ToastContext';
import { LanguageProvider } from './context/LanguageContext';
import UpdateGate from './components/UpdateGate';

function RequireAdmin({ children }) {
  const { user } = useAuth();
  if (!user?.permissions?.includes('admin_panel')) return <Navigate to="/" />;
  return children;
}

function RequireSupervisor({ children }) {
  const { user } = useAuth();
  if (!user?.permissions?.includes('supervisor_panel') && !user?.permissions?.includes('admin_panel')) return <Navigate to="/" />;
  return children;
}

// Old deep links (and the pre-routing-change Android build, bookmarks, and
// historical notification links) point at /app/*. The app now lives at the
// site root, so strip the /app prefix and forward to the new path.
function LegacyAppRedirect() {
  const location = useLocation();
  const rest = location.pathname.replace(/^\/app/, '') + location.search;
  return <Navigate to={rest || '/'} replace />;
}

function ForcePasswordReset() {
  const { user, authFetch, updateUser, logout } = useAuth();
  const [passwords, setPasswords] = useState({ current: '', newPw: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (passwords.newPw.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (passwords.newPw !== passwords.confirm) { setError('Passwords do not match'); return; }
    setSaving(true); setError('');
    try {
      const res = await authFetch('/api/users/profile/me', {
        method: 'PUT',
        body: JSON.stringify({ name: user.name, email: user.email, currentPassword: passwords.current, newPassword: passwords.newPw })
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to change password'); setSaving(false); return; }
      updateUser({ ...user, password_reset_required: false });
    } catch { setError('Failed to change password'); }
    setSaving(false);
  };

  const inputStyle = {
    width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid #e2e8f0',
    fontSize: 14, outline: 'none', boxSizing: 'border-box', background: '#f8fafc'
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f1f5f9', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 32, maxWidth: 420, width: '100%', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ width: 56, height: 56, borderRadius: 14, background: 'rgba(239,68,68,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 28 }}>
            🔒
          </div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1e293b' }}>Password Reset Required</h2>
          <p style={{ margin: '8px 0 0', fontSize: 14, color: '#64748b' }}>
            An administrator has required you to change your password before continuing.
          </p>
        </div>
        <form onSubmit={handleSubmit}>
          {error && (
            <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(220,38,38,0.06)', color: '#dc2626', fontSize: 13, marginBottom: 16, border: '1px solid rgba(220,38,38,0.2)' }}>{error}</div>
          )}
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4, display: 'block' }}>Current Password</label>
            <input type="password" required value={passwords.current} onChange={e => setPasswords(p => ({ ...p, current: e.target.value }))} placeholder="Enter your current password" style={inputStyle} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4, display: 'block' }}>New Password</label>
            <input type="password" required value={passwords.newPw} onChange={e => setPasswords(p => ({ ...p, newPw: e.target.value }))} placeholder="Min 6 characters" style={inputStyle} />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4, display: 'block' }}>Confirm New Password</label>
            <input type="password" required value={passwords.confirm} onChange={e => setPasswords(p => ({ ...p, confirm: e.target.value }))} placeholder="Re-enter new password" style={inputStyle} />
          </div>
          <button type="submit" disabled={saving} style={{
            width: '100%', padding: 14, borderRadius: 10, border: 'none', background: '#2563eb', color: '#fff',
            fontSize: 15, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1
          }}>{saving ? 'Changing Password...' : 'Change Password'}</button>
          <button type="button" onClick={logout} style={{
            width: '100%', padding: 12, borderRadius: 10, border: '1px solid #e2e8f0', background: 'none',
            color: '#64748b', fontSize: 13, fontWeight: 500, cursor: 'pointer', marginTop: 10
          }}>Sign Out</button>
        </form>
      </div>
    </div>
  );
}

// Bundled apps (the Electron desktop build and the Android WebView) have
// no hub in front of them to stamp X-Tenant-Code, so they can never
// auto-resolve a tenant from the URL. Detect them so we default to the
// company-code entry screen instead of the "not configured" hint.
function isBundledClient() {
  if (typeof window === 'undefined') return false;
  if (window.AndroidBridge) return true; // Android WebView JS interface (see MainActivity.java)
  const ua = (window.navigator && window.navigator.userAgent) || '';
  if (/Electron/i.test(ua)) return true;
  if (window.process && window.process.versions && window.process.versions.electron) return true;
  if (window.location && window.location.protocol === 'file:') return true;
  return false;
}

function TenantNotConfigured() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f1f5f9', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 32, maxWidth: 460, width: '100%', boxShadow: '0 4px 24px rgba(0,0,0,0.08)', textAlign: 'center' }}>
        <div style={{ width: 56, height: 56, borderRadius: 14, background: 'rgba(37,99,235,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 28 }}>
          🔧
        </div>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#1e293b' }}>Site not configured</h2>
        <p style={{ margin: '10px 0 20px', fontSize: 14, color: '#64748b', lineHeight: 1.55 }}>
          This site couldn't identify which company database to load. If you have a company code, enter it below to continue. Otherwise, contact your administrator.
        </p>
        <a href="/company" style={{
          display: 'inline-block', padding: '12px 22px', borderRadius: 10, background: '#2563eb', color: '#fff',
          fontSize: 14, fontWeight: 700, textDecoration: 'none'
        }}>Enter company code</a>
      </div>
    </div>
  );
}

function AppRoutes() {
  const { user, loading, companyCode, serverDisabled } = useAuth();
  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: '#94a3b8' }}>Loading...</div>;
  // Hub said this tenant's server is disabled — short-circuit the whole
  // app and show the disabled card. Routing/auth state is irrelevant
  // until the hub re-enables (the AuthContext poll clears this when it
  // flips back to enabled:true).
  if (serverDisabled) return <DisabledScreen {...serverDisabled} />;

  // Marketing/landing page now lives at "/landing" (served to hub-proxied
  // requests via the server-side canonical guard). The app itself lives at
  // the site root "/". When there's no tenant code we no longer default to
  // the company-code entry screen — the hub is expected to have resolved
  // the tenant from the request's Host (customer_custom_domains) and
  // dropped the tenant_code cookie for us. The entry screen is only
  // reachable at "/company" as a manual admin/switch flow; everything
  // else shows a "not configured" hint.
  if (!user && !companyCode) {
    // Bundled desktop / Android app has no hub in front of it to inject
    // X-Tenant-Code, so it always needs the user to enter a code first.
    const bundled = isBundledClient();
    return (
      <Routes>
        <Route path="/landing" element={<LandingPage />} />
        <Route path="/activate/:token" element={<ActivateAccount />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/company" element={<CompanyCode />} />
        <Route path="*" element={bundled ? <CompanyCode /> : <TenantNotConfigured />} />
      </Routes>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/landing" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/activate/:token" element={<ActivateAccount />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  if (user.password_reset_required) {
    return <ForcePasswordReset />;
  }

  return (
    <Routes>
      <Route path="/landing" element={<LandingPage />} />
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/activate/:token" element={<ActivateAccount />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      {/* Forward any lingering /app/* deep links to the new root paths. */}
      <Route path="/app/*" element={<LegacyAppRedirect />} />
      <Route path="/app" element={<Navigate to="/" replace />} />
      <Route path="/" element={<Layout />}>
        <Route index element={<Dashboard />} />
        <Route path="my-work" element={<MyWork />} />
        <Route path="work-orders" element={<WorkOrders />} />
        <Route path="work-orders/:id" element={<WorkOrderDetail />} />
        <Route path="assets" element={<Assets />} />
        <Route path="assets/:id" element={<AssetDetail />} />
        <Route path="reports" element={<Reports />} />
        <Route path="projects" element={<Projects />} />
        <Route path="projects/:id" element={<ProjectDetail />} />
        <Route path="my-team" element={<MyTeam />} />
        <Route path="credentials" element={<Credentials />} />
        <Route path="supervisor" element={<RequireSupervisor><SupervisorPanel /></RequireSupervisor>} />
        <Route path="admin" element={<RequireAdmin><AdminPanel /></RequireAdmin>} />
        <Route path="users" element={<RequireAdmin><ManageUsers /></RequireAdmin>} />
        <Route path="settings" element={<Settings />} />
        <Route path="notifications/new" element={<CreateNotification />} />
        <Route path="help" element={<HelpCentre />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

function ThemeProviderBridge({ children }) {
  const { authFetch, user } = useAuth();
  return (
    <ThemeProvider authFetch={authFetch} user={user}>
      {children}
    </ThemeProvider>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <UpdateGate />
      <LanguageProvider>
        <SiteProvider>
          <AuthProvider>
            <BrowserRouter>
              <ToastProvider>
                <ThemeProviderBridge>
                  <AppRoutes />
                </ThemeProviderBridge>
              </ToastProvider>
            </BrowserRouter>
          </AuthProvider>
        </SiteProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
}
